import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Skeleton,
  Divider,
  useTheme,
  Fab,
} from "@mui/material";
import {
  Visibility,
  ShoppingCart,
  Business,
  AttachMoney,
  Inventory,
  Close,
  CheckCircle,
} from "@mui/icons-material";
import { isUndefined } from "lodash";
import { RootState } from "@/store/type";
import { useSelector } from "react-redux";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { LANG, ROLE } from "@/types";
import { showToast } from "@/app/global-components/toaster";
import useLobby from "@/hooks/use-lobby";
import { useNavigate } from "react-router-dom";
import { routes } from "@/consts";
import useOrder from "@/hooks/use-order";
import useOffer from "@/hooks/useTender";

interface Product {
  productId: string;
  unitName: string;
  DeliveryMethod: string;
  quantity: number;
  unitPrice: number;
  vat: number;
  notice: string;
  attachment?: File | null;
  images?: string[] | null | undefined;
  company: {
    id: string;
    nameAr: string;
    CompanyNr: string;
  };
  tender: { title: string; id: string; status: string };
}

interface TenderQuotation {
  tender: { title: string; id: string; status: string };
  id: string;
  DeliveryMethod: string;
  status: string;
  products: Product[];
  company: {
    CompanyNr: string;
    id: string;
    nameAr: string;
  };
}

interface QuatationsStatisticsProps {
  offerID?: string;
  offerStatus?: string;
}

const MarkteComponent: React.FC<QuatationsStatisticsProps> = ({
  offerID,
  offerStatus,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const theme = useTheme();
  const {
    getListtenderQuotationsByOfferId,
    getListtenderQuotationsByOfferIdMy,
  } = useLobby();
  const [listOffers, setListOffers] = useState<TenderQuotation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const navigate = useNavigate();
  const { createOrder } = useOrder();
  const { acceptQuatation } = useOffer();

  // Fetch quotations data
  useEffect(() => {
    const fetchQuotations = async () => {
      if (!offerID) return;

      try {
        setIsLoading(true);
        let response;

        if (userRole === ROLE.BUYER) {
          response = await getListtenderQuotationsByOfferId(offerID);
        } else {
          response = await getListtenderQuotationsByOfferIdMy(offerID);
        }

        if (response?.data) {
          setListOffers(response.data);
        } else {
          setListOffers([]);
        }
      } catch (error) {
        console.error("Error fetching offers:", error);
        showToast(t("alerts.error.fetching"), "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuotations();
  }, [offerID, userRole]);

  // Handle product selection
  const handleProductSelect = (product: Product) => {
    if (offerStatus === "Opened" || offerStatus === "Accepted") return;

    setSelectedProducts((prev) => {
      const isSelected = prev.some((p) => p.productId === product.productId);
      if (isSelected) {
        return prev.filter((p) => p.productId !== product.productId);
      } else {
        return [
          ...prev,
          {
            ...product,
            DeliveryMethod:
              listOffers.find((offer) =>
                offer.products.some(
                  (prod) => prod.productId === product.productId
                )
              )?.DeliveryMethod ?? "",
          },
        ];
      }
    });
  };

  // Handle product details view
  const handleShowProductDetails = (
    product: Product,
    offer: TenderQuotation
  ) => {
    setSelectedProduct({
      ...product,
      company: offer.company,
      tender: offer.tender,
    });
    setIsDialogOpen(true);
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedProduct(null);
  };

  // Handle create order
  const handleCreateOrder = async () => {
    if (!selectedProducts.length || !listOffers.length) return;

    const orderData = {
      title: "Order for Tender",
      deliverDate: new Date(
        new Date().getTime() + 7 * 24 * 60 * 60 * 1000
      ).toISOString(),
      type: "Tiny",
      status: "Planning",
      region: myCompany.region,
      city: myCompany.city,
      address: myCompany.address,
      products: selectedProducts.map((item) => ({
        itemId: item.productId,
        item: item.unitName,
        quantity: item.quantity,
        deliveryMethod: item.DeliveryMethod,
        discount: 0,
        price: item.unitPrice,
        vat: item.vat || 15,
        notice: item.notice || "",
        image: item.images?.[0],
        companyId: listOffers.find(
          (offer) =>
            offer.products.find(
              (product) => product.productId === item.productId
            )?.productId === item.productId
        )?.company?.id,
        tender: listOffers[0]?.tender?.id,
        quatationId: listOffers.find(
          (offer) =>
            offer.products.find(
              (product) => product.productId === item.productId
            )?.productId === item.productId
        )?.id,
      })),
      tenderId: listOffers[0]?.tender?.id,
      userId: authUser?.id,
      companyId: myCompany?.id,
      Sendedproducts: [],
      OrderNr: myCompany ? myCompany.orderNr + 1 : 1,
    };

    try {
      setIsPlacingOrder(true);
      const response = await createOrder(orderData);
      if (response?.isSuccess) {
        showToast(t("Alert.OrderCreated"), "success");
        if (listOffers[0]?.id) {
          await acceptQuatation(listOffers[0].id);
        }
        navigate(`${routes.order}/${response.data.id}`);
      } else {
        showToast(t("Alert.OrderCreationError"), "warning");
      }
    } catch (error) {
      showToast(t("Alert.OrderTryAgain"), "warning");
      console.error("Error creating order:", error);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  // Calculate total value of selected products
  // const totalValue = selectedProducts.reduce(
  //   (sum, product) => sum + product.unitPrice * product.quantity,
  //   0
  // );

  // Filter out canceled offers
  const activeOffers = listOffers.filter(
    (offer) => offer.status !== "Canceled"
  );

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          {t("offers.allProducts")}
        </Typography>
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item}>
              <Skeleton variant="rectangular" height={300} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,

            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Inventory />
          {t("offers.allProducts")}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {activeOffers.length} {t("general.items")} {t("status.opened")}
        </Typography>
      </Box>

      {/* Products Grid */}
      {activeOffers.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            {t("general.noDataMatchingCriteria")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("offers.noAvailable")}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {activeOffers.map((offer) =>
            offer.products.map((product) => {
              const isSelected = selectedProducts.some(
                (p) => p.productId === product.productId
              );

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={product.productId}>
                  <Card
                    sx={{
                      height: "100%",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      border: isSelected
                        ? `2px solid ${theme.palette.primary.main}`
                        : "1px solid #e0e0e0",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: theme.shadows[8],
                        borderColor: theme.palette.primary.main,
                      },
                      position: "relative",
                      overflow: "visible",
                    }}
                    onClick={() => handleProductSelect(product)}
                  >
                    {/* Selection Badge */}
                    {isSelected && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          zIndex: 1,
                        }}
                      >
                        <Chip
                          icon={<CheckCircle />}
                          label={t("actions.accept")}
                          color="primary"
                          size="small"
                        />
                      </Box>
                    )}

                    {/* Product Image */}
                    <CardMedia
                      component="img"
                      height="200"
                      image={product.images?.[0] || ""}
                      alt={product.unitName}
                      sx={{
                        objectFit: "cover",
                        backgroundColor: theme.palette.grey[100],
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        target.nextElementSibling?.classList.remove("hidden");
                      }}
                    />

                    {/* Fallback for no image */}
                    {!product.images?.[0] && (
                      <Box
                        sx={{
                          height: 200,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: theme.palette.grey[100],
                          color: theme.palette.text.secondary,
                        }}
                      >
                        <Typography variant="body2">
                          {t("images.noImage")}
                        </Typography>
                      </Box>
                    )}

                    <CardContent sx={{ p: 2 }}>
                      {/* Product Name */}
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          lineHeight: 1.2,
                          height: "2.4em",
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {product.unitName}
                      </Typography>

                      {/* Company Info */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Business
                          sx={{
                            fontSize: 16,
                            mr: 0.5,
                            color: "text.secondary",
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {t("NR.Supplier")}: {offer.company.CompanyNr}
                        </Typography>
                      </Box>

                      {/* Price */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <AttachMoney
                          sx={{ fontSize: 16, mr: 0.5, color: "primary.main" }}
                        />
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                          }}
                        >
                          {product.unitPrice} {t("currency")}
                        </Typography>
                      </Box>

                      {/* Quantity */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {t("checkout.fields.quantity")}: {product.quantity}
                      </Typography>

                      {/* Actions */}
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          startIcon={<Visibility />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowProductDetails(product, offer);
                          }}
                          sx={{
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            "&:hover": {
                              borderColor: theme.palette.primary.dark,
                              backgroundColor:
                                theme.palette.primary.main + "10",
                            },
                          }}
                        >
                          {t("general.view")}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })
          )}
        </Grid>
      )}

      {/* Floating Action Button for Order */}
      {selectedProducts.length > 0 && (
        <Fab
          variant="extended"
          className="!bg-[#18181b] !text-white"
          onClick={handleCreateOrder}
          disabled={isPlacingOrder}
          sx={{
            position: "fixed",
            bottom: 24,
            right: locale === LANG.EN ? 24 : "auto",
            left: locale === LANG.EN ? "auto" : 24,
            zIndex: 1000,
          }}
        >
          <ShoppingCart sx={{ mr: 1 }} />
          {isPlacingOrder
            ? t("actions.creatingOrder")
            : `${t("actions.createOrder")} (${selectedProducts.length})`}
        </Fab>
      )}

      {/* Product Details Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}05 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t("products.details")}
          </Typography>
          <IconButton
            onClick={handleCloseDialog}
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedProduct && (
            <Grid container spacing={3}>
              {/* Product Image */}
              <Grid item xs={12} md={6}>
                {selectedProduct.images?.[0] ? (
                  <Box
                    component="img"
                    src={selectedProduct.images[0]}
                    alt={selectedProduct.unitName}
                    sx={{
                      width: "100%",
                      height: 300,
                      objectFit: "cover",
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: 300,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: theme.palette.grey[100],
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t("images.noImage1")}
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Product Details */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                    {selectedProduct.unitName}
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  {/* Company Information */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      {t("checkout.fields.company")}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Business
                        sx={{ fontSize: 20, color: "text.secondary" }}
                      />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedProduct.company.CompanyNr}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Price */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      className="!text-black"
                      sx={{ mb: 0.5 }}
                    >
                      {t("item.fields.price")}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AttachMoney sx={{ fontSize: 20, color: "#000" }} />
                      <Typography
                        variant="h6"
                        className="!text-black"
                        sx={{ fontWeight: 700 }}
                      >
                        {selectedProduct.unitPrice} {t("currency")}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Quantity */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      {t("checkout.fields.quantity")}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedProduct.quantity}
                    </Typography>
                  </Box>

                  {/* VAT */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      {t("item.fields.vat")}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedProduct.vat || 15}%
                    </Typography>
                  </Box>

                  {/* Notes */}
                  {selectedProduct.notice && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        {t("orders.fields.notes")}
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                        {selectedProduct.notice}
                      </Typography>
                    </Box>
                  )}

                  {/* Total Value */}
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      backgroundColor: theme.palette.grey[50],
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      {t("orders.fields.totalPrice")}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: "#000" }}
                    >
                      {(
                        selectedProduct.unitPrice *
                        selectedProduct.quantity *
                        (1 + (selectedProduct.vat || 15) / 100)
                      ).toFixed(2)}{" "}
                      {t("currency")}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCloseDialog}
            sx={{
              borderColor: theme.palette.grey[400],
              color: theme.palette.text.primary,
              "&:hover": {
                borderColor: theme.palette.grey[600],
                backgroundColor: theme.palette.grey[50],
              },
            }}
          >
            {t("actions.close")}
          </Button>

          {offerStatus !== "Opened" && offerStatus !== "Accepted" && (
            <Button
              variant="contained"
              onClick={() => {
                if (selectedProduct) {
                  handleProductSelect(selectedProduct);
                }
                handleCloseDialog();
              }}
              startIcon={<ShoppingCart />}
              sx={{
                backgroundColor: "#18181b",
              }}
            >
              {selectedProducts.some(
                (p) => p.productId === selectedProduct?.productId
              )
                ? t("actions.delete")
                : t("actions.addToCart")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarkteComponent;
